import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-monitoring-ultrasound"),
  kind: "test",
  tags: [
    "clinic-visit",
    "her",
    "during-cycle",
    "ivf",
    "egg-freezing",
    "testing",
  ],
  title: "Monitoring Ultrasound",
  subtitle:
    "Transvaginal ultrasound to track follicle growth and uterine lining",
  body: {
    testName: "Monitoring Ultrasound (Follicle Check)",
    category: "monitoring",
    whatItMeasures:
      "Measures the number and size of developing follicles and the thickness of the uterine lining during ovarian stimulation. These scans guide medication dosing, trigger timing, and whether the response is safe to continue.",
    howItWorks:
      "A slender ultrasound probe is gently inserted into the vagina to visualize the ovaries and uterus. The sonographer or physician measures each visible follicle in millimeters and records the endometrial lining thickness. The images appear on a screen in real time, and the whole scan typically takes five to ten minutes. Follicles often grow about 1-2 mm per day during stimulation, and lead follicles around 18-20 mm are often considered trigger-ready depending on the protocol. Based on the results, your care team may adjust your medication doses, add new medications, or schedule your next monitoring visit. Most stimulation cycles require three to six monitoring appointments spaced one to three days apart. Empty your bladder before the scan for comfort and wear comfortable clothing that is easy to change out of from the waist down. Continue all medications as prescribed unless told otherwise.",
    quickFacts: [
      { kind: "sample", value: "Transvaginal ultrasound" },
      { kind: "time", value: "5-10 min scan" },
      { kind: "pain", value: "Pressure, usually mild" },
      { kind: "prep", value: "Empty bladder; keep meds as directed" },
      { kind: "results", value: "During the appointment" },
    ],
    costRange: { low: 200, high: 500 },
    atHome: false,
    clinicRequired: true,
    cycleDay: "Roughly cycle days 6-12 (stimulation phase)",
    dataPoints: [
      makeId("dp-follicle-count"),
      makeId("dp-follicle-size"),
      makeId("dp-endometrial-thickness"),
    ],
    section: "cycle-monitoring",
  },
  sources: [
    {
      title: "ESHRE guideline: ovarian stimulation for IVF/ICSI",
      authors: "ESHRE Guideline Group on Ovarian Stimulation",
      journal: "Human Reproduction Open",
      year: 2020,
      url: "https://academic.oup.com/hropen/article/2020/2/hoaa009/5826933",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Individualization of controlled ovarian stimulation in IVF using ovarian reserve markers: from theory to practice",
      authors: "La Marca A, Sunkara SK",
      journal: "Human Reproduction Update",
      year: 2014,
      url: "https://academic.oup.com/humupd/article-lookup/doi/10.1093/humupd/dmt037",
      evidenceLevel: "review",
    },
  ],
  readTimeMinutes: 3,
};

registerBlock(test);
